import type React from 'react';
import { useState, useCallback, useEffect, useRef } from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import { Button, mergeClasses } from '@fluentui/react-components';
import { NavigationRegular, DismissRegular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { useStatusPanelMode } from 'contexts';
import { useIsMobile } from 'hooks';
import { useAppShellStyles } from './AppShell.styles';
import { LanguageSelector } from './LanguageSelector';

interface NavItem {
  labelKey: string;
  to: string;
}

const NAV_ITEMS: NavItem[] = [
  { labelKey: 'app.navigation.statusPanel', to: '/status-panel' },
  { labelKey: 'app.navigation.logManagement', to: '/log-management' },
  { labelKey: 'app.navigation.onboarding', to: '/onboarding' },
];

const MOBILE_SIDEBAR_WIDTH = 280;

export const AppShell: React.FC = () => {
  const styles = useAppShellStyles();
  const { t } = useTranslation();
  const { isWallScreen } = useStatusPanelMode();
  const isMobile = useIsMobile();
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [isSwipeActive, setIsSwipeActive] = useState(false);
  const [swipeTranslateX, setSwipeTranslateX] = useState<number | null>(null);
  const [swipeBackdropOpacity, setSwipeBackdropOpacity] = useState(0);
  const swipeStateRef = useRef({
    tracking: false,
    readyToOpen: false,
    startX: 0,
    startY: 0,
  });

  // Close sidebar when viewport grows to desktop size
  useEffect(() => {
    if (!isMobile) {
      setIsNavOpen(false);
      setIsSwipeActive(false);
      setSwipeTranslateX(null);
      setSwipeBackdropOpacity(0);
    }
  }, [isMobile]);

  // Prevent body scroll when sidebar is open
  useEffect(() => {
    if (isNavOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isNavOpen]);

  const handleSidebarLinkClick = useCallback(() => {
    setIsNavOpen(false);
  }, []);

  const handleTouchStart = useCallback(
    (event: React.TouchEvent<HTMLDivElement>) => {
      if (!isMobile || isNavOpen) {
        return;
      }

      const touch = event.touches[0];
      const leftActivationZone = window.innerWidth / 3;
      swipeStateRef.current.tracking = touch.clientX <= leftActivationZone;
      swipeStateRef.current.readyToOpen = false;
      swipeStateRef.current.startX = touch.clientX;
      swipeStateRef.current.startY = touch.clientY;

      if (swipeStateRef.current.tracking) {
        setIsSwipeActive(true);
        setSwipeTranslateX(-MOBILE_SIDEBAR_WIDTH);
        setSwipeBackdropOpacity(0);
      }
    },
    [isMobile, isNavOpen],
  );

  const handleTouchMove = useCallback((event: React.TouchEvent<HTMLDivElement>) => {
    if (!swipeStateRef.current.tracking) {
      return;
    }

    const touch = event.touches[0];
    const deltaX = touch.clientX - swipeStateRef.current.startX;
    const deltaY = Math.abs(touch.clientY - swipeStateRef.current.startY);

    if (deltaY > 48) {
      swipeStateRef.current.tracking = false;
      swipeStateRef.current.readyToOpen = false;
      setIsSwipeActive(false);
      setSwipeTranslateX(null);
      setSwipeBackdropOpacity(0);
      return;
    }

    const horizontalDrag = Math.max(0, Math.min(MOBILE_SIDEBAR_WIDTH, deltaX));
    const progress = horizontalDrag / MOBILE_SIDEBAR_WIDTH;

    setSwipeTranslateX(-MOBILE_SIDEBAR_WIDTH + horizontalDrag);
    setSwipeBackdropOpacity(progress * 0.45);
    swipeStateRef.current.readyToOpen = horizontalDrag > MOBILE_SIDEBAR_WIDTH * 0.35;
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (swipeStateRef.current.tracking && swipeStateRef.current.readyToOpen) {
      setIsNavOpen(true);
    }

    swipeStateRef.current.tracking = false;
    swipeStateRef.current.readyToOpen = false;
    setIsSwipeActive(false);
    setSwipeTranslateX(null);
    setSwipeBackdropOpacity(0);
  }, []);

  const isSidebarVisible = isNavOpen || isSwipeActive;
  const sidebarInlineStyle =
    isSwipeActive && !isNavOpen && swipeTranslateX !== null
      ? {
          transform: `translateX(${swipeTranslateX}px)`,
          transitionDuration: '0ms',
        }
      : undefined;
  const backdropInlineStyle =
    isSwipeActive && !isNavOpen
      ? {
          opacity: swipeBackdropOpacity,
        }
      : undefined;

  // In wall-screen mode, render content without the header/nav shell
  if (isWallScreen) {
    return (
      <div className={styles.wallScreenRoot}>
        <Outlet />
      </div>
    );
  }

  return (
    <div
      className={styles.root}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <header className={styles.header}>
        {isMobile ? (
          <div className={styles.mobileHeaderRow}>
            <Button
              appearance="subtle"
              size="large"
              icon={<NavigationRegular />}
              onClick={() => setIsNavOpen(true)}
              className={styles.burgerButton}
              aria-label={t('app.openNavigation')}
            />
            <NavLink to="/" className={styles.mobileHeaderTitle}>
              {t('app.title')}
            </NavLink>
            <LanguageSelector compact />
          </div>
        ) : (
          <>
            <NavLink to="/" className={styles.headerTitle}>
              {t('app.title')}
            </NavLink>
            <nav className={styles.nav}>
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    mergeClasses(styles.navLink, isActive && styles.navLinkActive)
                  }
                >
                  {t(item.labelKey)}
                </NavLink>
              ))}
              <div className={styles.navDivider} />
              <LanguageSelector className={styles.languageSelector} />
            </nav>
          </>
        )}
      </header>

      {/* Mobile sidebar overlay */}
      {isMobile && (
        <>
          {isSidebarVisible && (
            <div
              className={styles.navBackdrop}
              onClick={() => setIsNavOpen(false)}
              style={backdropInlineStyle}
              aria-hidden="true"
            />
          )}
          <div
            className={mergeClasses(styles.navSidebar, isNavOpen && styles.navSidebarOpen)}
            style={sidebarInlineStyle}
            aria-hidden={!isNavOpen}
          >
            <div className={styles.navSidebarHeader}>
              <span className={styles.navSidebarTitle}>{t('app.title')}</span>
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                onClick={() => setIsNavOpen(false)}
                className={styles.navSidebarClose}
                aria-label={t('app.closeNavigation')}
              />
            </div>
            <nav className={styles.navSidebarLinks}>
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={handleSidebarLinkClick}
                  className={({ isActive }) =>
                    mergeClasses(styles.navSidebarLink, isActive && styles.navSidebarLinkActive)
                  }
                >
                  {t(item.labelKey)}
                </NavLink>
              ))}
            </nav>
          </div>
        </>
      )}

      <main className={styles.content}>
        <Outlet />
      </main>
    </div>
  );
};
